import { AdventureType, CouponType } from '@prisma/client';
import z from 'zod'

const passwordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export const signUpSchema = z.object({
  email: z.string().email("Invalid email address"),
  fullname: z.string(),
  password: z.string()
    .min(8, "Password must be at least 8 characters long")
    .regex(
      passwordValidationRegex,
      "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  phone: z.string(),
})

export type SignUpSchema = z.infer<typeof signUpSchema>


export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string()
})

export type LogInSchema = z.infer<typeof loginSchema>

export const destinationSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  slug: z.string().regex(
    /^[a-z0-9-]+$/,
    "Slug must contain only lowercase letters, numbers, and hyphens, with no spaces or other special characters"
  ),
  subtext: z.string(),
  destination: z.string(),
  about_preview: z.string().optional(),
  about_destination: z.string().optional(),
  curatedPosition: z.string().optional(),
  destinationCategoriesId: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  metaTitle: z.string().optional(),
  position: z.string().optional(),
})

export type DestinationSchema = z.infer<typeof destinationSchema>

export const destinationCategorySchema = z.object({
  title: z.string(),
  destinations: z.array(z.string()).refine((value) => value.some((item) => item), {
    message: "You have to select at least one service.",
  }),
});
export type DestinationCategorySchema = z.infer<typeof destinationCategorySchema>

export const paritalTripForm = z.object({
  title: z.string(),
  slug: z.string().regex(
    /^[a-z0-9-]+$/,
    "Slug must contain only lowercase letters, numbers, and hyphens, with no spaces or other special characters"
  ),
  number_of_nights: z.string(),
  number_of_days: z.string(),
  location: z.string(),
  sudo_price: z.string().optional(),
  main_price: z.string()
});

export type PartialTripFormType = z.infer<typeof paritalTripForm>

export const tripSchema = z.object({
  title: z.string(),
  slug: z.string().regex(
    /^[a-z0-9-]+$/,
    "Slug must contain only lowercase letters, numbers, and hyphens, with no spaces or other special characters"
  ),
  number_of_nights: z.string(),
  number_of_days: z.string(),
  location: z.string(),
  sudo_price: z.string().optional(),
  main_price: z.string().optional(),
  isFeatured: z.boolean().optional(),
  enabled_booking: z.boolean().optional(),
  enabledPartialPay: z.boolean().optional(),
  destinationId: z.string().optional(),
  tripCategoryIds: z.array(z.string()).optional(),
  trekCategoryIds: z.array(z.string()).optional(),
  age: z.string().optional(),
  type: z.string().optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  about_preview: z.string().optional(),
  about_trip: z.string().optional(),
  things_to_carry: z.string().optional(),
  pickup_transport: z.string().optional(),
  rules_regulations: z.string().optional(),
  trip_highlights: z.array(z.string().optional()).optional(),
  other_highlights: z.array(z.string().optional()).optional(),
  inclusions: z.array(z.string().optional()).optional(),
  exclusions: z.array(z.string().optional()).optional(),
  position: z.string().optional(),

  itinerary: z.array(z.object({
    title: z.string(),
    day: z.string().regex(/^\d+$/, "Day must be a numeric string"),
    description: z.string(),
    meals: z.array(z.string()),
    accommodations: z.array(z.string()),
    new_meals: z.string().optional(),
    new_accommodations: z.string().optional(),
    note: z.string().optional()
  })),

  // Costing array of objects
  costing: z.array(z.object({
    title: z.string().optional(),
    price: z.string().optional(),
  })),

  // Addons array of objects
  addons: z.array(z.object({
    title: z.string().optional(),
    price: z.string().optional(),
    note: z.string().optional(),
  })),
  // FAQ array of objects
  faqs: z.array(z.object({
    question: z.string().optional(),
    answer: z.string().optional(),
  })).optional()
});

export type TripSchema = z.infer<typeof tripSchema>

export const batchesSchema = z.object({
  tripId: z.string(),
  city: z.string(),
  main_price: z.string(),
  sudo_price: z.string().optional(),
  pickup_location: z.string().optional(),
});

export type BatchesSchema = z.infer<typeof batchesSchema>

export const availableDateSchema = z.object({
  date_range: z.object({
    from: z.coerce.date(),
    to: z.coerce.date(),
  }),
  expiration_date_time: z.coerce.date(),
  available_seats: z.string(),
  price: z.string(),
  enabled_booking: z.boolean().default(true),
  addons: z.array(z.object({
    title: z.string().optional(),
    price: z.string().optional(),
    note: z.string().optional(),
  })),
});

export type AvailableDateSchema = z.infer<typeof availableDateSchema>

export const transportOptionSchema = z.object({
  transport: z.string(),
  price_per_person: z.string(),
  max_capacity: z.string(),
  availableid: z.string()
});

export type TransportOptionSchema = z.infer<typeof transportOptionSchema>

export const tripEnquiryFormSchema = z.object({
  name: z.string().min(3, {
    message: "name must be at least 2 characters",
  }),
  email: z.string().email(),
  phone: z.string(),
  destination: z.string()
})

export type TripEnquiryFormSchema = z.infer<typeof tripEnquiryFormSchema>

export const contactUsFromSchema = z.object({
  name: z.string().min(3, {
    message: "name must be at least 2 characters",
  }),
  email: z.string().email(),
  phone: z.string().min(10, {
    message: "phone number must contain 10 numbers",
  }),
  month: z.string({
    required_error: "Please select any month to display.",
  }),
  destination: z.string(),
  message: z.string().optional(),
})

export type ContactUsFromSchema = z.infer<typeof contactUsFromSchema>


export const getInTouchSchema = z.object({
  name: z.string().min(3, {
    message: "name must be at least 2 characters",
  }),
  email: z.string().email(),
  phone: z.string().min(10, {
    message: "phone number must contain 10 numbers",
  }),
  destination: z.string()
})

export type GetInTouchSchema = z.infer<typeof getInTouchSchema>

export const enquiryFormSchema = z.object({
  name: z.string().min(3, {
    message: "name must be at least 2 characters",
  }),
  phone: z.string().min(10, {
    message: "phone number must contain 10 numbers",
  }),
  city: z.string().min(3, {
    message: "Please enter your city name"
  }),
  month: z.string({
    required_error: "Please select any month to display",
  }),
  tripName: z.string(),
  message: z.string().optional(),
})

export type EnquiryFormSchema = z.infer<typeof enquiryFormSchema>

export const bookEnquirySchema = z.object({
  city: z.string(),
  pickup_location: z.string().optional(),
  end_date: z.date(),
  start_date: z.date(),
  total_amount: z.number(),
  total_members: z.number(),
  transport_details: z.array(z.object({
    transport_optionns: z.string(),
    transport_price: z.number(),
    number_of_travelors: z.number(),
  })),
  addon_details: z.any().optional(),
  gst: z.number().optional(),
  grand_total: z.number(),
  trip_title: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string()
})

export type BookEnquirySchema = z.infer<typeof bookEnquirySchema>

export const checkoutSchema = z.object({
  amount: z.number(),
  phone: z.string().optional(),
  userid: z.string(),
  // method: z.enum(['netbanking', 'card']).default('card')
})

export const verificationSchema = z.object({
  razorpay_order_id: z.string(),
  razorpay_payment_id: z.string(),
  razorpay_signature: z.string(),
  city: z.string(),
  pickup_location: z.string().optional(),
  end_date: z.date(),
  start_date: z.date(),
  total_amount: z.number(),
  total_members: z.number(),
  transport_details: z.array(z.object({
    transport_optionns: z.string(),
    transport_price: z.number(),
    number_of_travelors: z.number(),
  })),
  addon_details: z.any().optional(),
  tripId: z.string().optional(),
  userId: z.string(),
  gst: z.number().optional(),
  grand_total: z.number(),
  recieved_amount: z.number(),
  remaining_amount: z.number(),
  discount_amount: z.number().optional(),
  couponId: z.string().optional(),
  trip_title: z.string(),
  adventureType: z.nativeEnum(AdventureType)
})

export const partialCategorySchema = z.object({
  title: z.string().min(1),
  slug: z.string().regex(
    /^[a-z0-9-]+$/,
    "Slug must contain only lowercase letters, numbers, and hyphens, with no spaces or other special characters"
  ),
  subtext: z.string()
});

export type PartialCategorySchema = z.infer<typeof partialCategorySchema>

export const categorySchema = z.object({
  title: z.string().min(1),
  slug: z.string().regex(
    /^[a-z0-9-]+$/,
    "Slug must contain only lowercase letters, numbers, and hyphens, with no spaces or other special characters"
  ),
  subtext: z.string(),
  position: z.string().min(1),
  curatedPosition: z.string().min(1),
  metaTitle: z.string().min(1),
  metaDescription: z.string(),
  metaKeywords: z.string()
})

export type CategorySchema = z.infer<typeof categorySchema>


export const bannerSchema = z.object({
  image_url: z.string().optional(),
  link: z.string(),
  position: z.string()
})

export type BannerSchema = z.infer<typeof bannerSchema>

export const couponSchema = z.object({
  code: z.string(),
  discount_percent: z.string().regex(/^\d+$/, "discount percentage must be a numeric string"),
  flat_discount: z.string().regex(/^\d+$/, "flat discount must be a numeric string"),
  expiration_date: z.preprocess(
    (val) => (typeof val === 'string' ? new Date(val) : val),
    z.date()
  ),
  max_redemption: z.string().min(1).regex(/^\d+$/, "max redemption must be a numeric string"),
  min_expendature: z.string().min(1).regex(/^\d+$/, "min expendature must be a numeric string"),
  type: z.nativeEnum(CouponType),
  trips: z.array(z.string()).optional(),
  visibility: z.boolean(),
  description: z.string().optional(),
})

export type CouponSchema = z.infer<typeof couponSchema>

export const reviewSchema = z.object({
  name: z.string().min(1),
  designation: z.string().min(1),
  review: z.string(),
  image_url: z.string().optional(),
  rating: z.string(),
  position: z.string().optional()
});

export type ReviewSchema = z.infer<typeof reviewSchema>

export const guidesSchema = z.object({
  name: z.string().min(1),
  designation: z.string().min(1),
  content: z.string(),
  image_url: z.string().optional(),
  position: z.string().optional()
});

export type GuidesSchema = z.infer<typeof guidesSchema>

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters long' })
      .regex(/[a-zA-Z0-9]/, { message: 'Password must be alphanumeric' }),
    confirmPassword: z.string(),
    resetToken: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  })

export type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>

export const crmLeadSchema = z.object({
  name: z.string().min(3, {
    message: "name must be at least 2 characters",
  }),
  destination: z.string(),
  email: z.string().email(),
  phone: z.string().min(10, {
    message: "phone number must contain 10 numbers",
  }),
})
export type CrmLeadSchema = z.infer<typeof crmLeadSchema>

export const profileUpdateSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string(),
  alternateNo: z.string().optional(),
  gender: z.enum(["MALE", "FEMALE"]),
  address: z.string().optional(),
})

export type ProfileUpdateSchema = z.infer<typeof profileUpdateSchema>